package com.evoapps.evoshift.domain.usecase.shift

import com.evoapps.evoshift.domain.util.AppClock
import com.evoapps.evoshift.domain.model.BreakRules
import com.evoapps.evoshift.domain.model.ClockAction
import com.evoapps.evoshift.domain.model.ClockOutcome
import com.evoapps.evoshift.domain.model.GeoLocation
import com.evoapps.evoshift.domain.model.GeofenceDecision
import com.evoapps.evoshift.domain.model.GeofenceRules
import com.evoapps.evoshift.domain.model.GeofenceSettings
import com.evoapps.evoshift.domain.model.LocationReading
import com.evoapps.evoshift.domain.model.LocationUnavailable
import com.evoapps.evoshift.domain.model.RosterIds
import com.evoapps.evoshift.domain.model.Shift
import com.evoapps.evoshift.domain.model.TimeRange
import com.evoapps.evoshift.domain.repository.ShiftRepository
import com.evoapps.evoshift.domain.repository.WorkplaceRepository
import kotlinx.datetime.LocalDateTime
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toInstant
import kotlinx.datetime.toLocalDateTime
import kotlin.time.Clock
import kotlin.time.Instant

/** What the clock-in button can do right now for the signed-in employee. */
sealed interface ClockStatus {
    /** No shift to start or finish now. */
    data object Idle : ClockStatus

    /** A shift starts soon or has started: the employee may clock in. */
    data class CanClockIn(val shift: Shift) : ClockStatus

    /** Clocked in at [since] ("HH:mm"), not out yet; SH-116: on a break since [breakSince] ("HH:mm"). */
    data class Working(val shift: Shift, val since: String, val breakSince: String? = null) : ClockStatus {
        val onBreak: Boolean get() = breakSince != null
    }

    /** Today's shift is done: worked from [start] to [end] ("HH:mm"). */
    data class Done(val shift: Shift, val start: String, val end: String) : ClockStatus
}

class ClockException(
    val reason: Reason,
    /** [Reason.OUTSIDE_AREA]: how far the person was, and the workplace's radius (metres). */
    val distanceMeters: Int? = null,
    val radiusMeters: Int? = null,
    /** [Reason.LOCATION_REQUIRED]: why there was no location. */
    val locationUnavailable: LocationUnavailable? = null
) : Exception("Clock: $reason") {
    enum class Reason {
        NOT_YOUR_SHIFT, TOO_EARLY, ALREADY_CLOCKED_IN, NOT_CLOCKED_IN,
        /** SH-106 Block mode: farther from the workplace than its radius. */
        OUTSIDE_AREA,
        /** SH-106 Block mode: no location (denied, no fix, a device without one). */
        LOCATION_REQUIRED,
        /** SH-116: a break is already going on / none is to end / the shift has the most breaks allowed. */
        ALREADY_ON_BREAK, NOT_ON_BREAK, TOO_MANY_BREAKS,
        /** SH-134, a clock-in without a shift: too early for one's own later shift / today's is finished. */
        NO_SHIFT_NOW, ALREADY_DONE,
        /** SH-165: the clock-out is older than [ClockRules.UNDO_MINUTES], or not the person's own. */
        UNDO_TOO_LATE
    }
}

/** SH-106: a workplace's geofence that applies to a clock (mode on and a location to measure from). */
data class ActiveGeofence(val settings: GeofenceSettings, val location: GeoLocation)

/**
 * Clocking in and out records the shift's actual start and end (local time, to the minute), which
 * the daily records use for actual labour cost. Clocking in opens [EARLY_MINUTES] before the
 * planned start and stays open until the planned end; clocking out is possible any time after.
 * Only the actual times are written (Firestore rules let the assignee change nothing else).
 * The rules themselves are [ClockRules], which the watch (SH-99) applies to its own copy.
 *
 * SH-106: where the shift's workplace has a geofence ([ActiveGeofence]), the device's location
 * is read once ([readLocation], at the tap) and the clock goes through the clockShift function,
 * which checks it again and stores only the result and the distance; the rules then refuse the
 * person's own direct writes. Without [workplaceRepository] (tests, older wiring) no geofence applies.
 */
class ClockInClockOutUseCase(
    private val shiftRepository: ShiftRepository,
    private val now: () -> Instant = { AppClock.now() },
    private val timeZone: () -> TimeZone = { TimeZone.currentSystemDefault() },
    private val workplaceRepository: WorkplaceRepository? = null
) {
    private fun nowLocal(): LocalDateTime = now().toLocalDateTime(timeZone())

    /**
     * The button's state for [userId]'s shifts (pure, from the shifts already loaded). SH-134: the
     * shifts they cover for a colleague count as theirs; their own shift a colleague covers does not.
     */
    fun status(myShifts: List<Shift>, userId: String): ClockStatus {
        val mine = myShifts.filter { it.isClockedBy(userId) && it.isPublished }
        return when (val pick = ClockRules.pick(mine, nowLocal(), timeZone(), shiftTimes)) {
            ClockPick.Idle -> ClockStatus.Idle
            is ClockPick.CanClockIn -> ClockStatus.CanClockIn(pick.shift)
            is ClockPick.Working -> ClockStatus.Working(pick.shift, pick.since, BreakRules.open(pick.shift.breaks)?.start?.hhmm())
            is ClockPick.Done -> ClockStatus.Done(pick.shift, pick.start, pick.end)
        }
    }

    /** The geofence [shift]'s clock goes through, or null where none applies (SH-106). */
    suspend fun geofenceOf(shift: Shift): ActiveGeofence? {
        val repository = workplaceRepository ?: return null
        val workplace = repository.getWorkplace(RosterIds.workplaceIdOf(shift.rosterId)).getOrThrow()
        val location = workplace.location ?: return null
        return if (GeofenceRules.isActive(workplace.settings.geofence, location)) ActiveGeofence(workplace.settings.geofence, location) else null
    }

    /**
     * Clocks in. [readLocation] reads the device's position once; it is called only where a
     * geofence applies, and null means the device has none (the watch, the desktop).
     */
    suspend fun clockIn(
        shiftId: String,
        userId: String,
        readLocation: (suspend () -> LocationReading)? = null
    ): Result<ClockOutcome> = runCatching {
        val shift = shiftRepository.getShift(shiftId).getOrThrow()
        if (!shift.isClockedBy(userId)) throw ClockException(ClockException.Reason.NOT_YOUR_SHIFT)
        if (shift.actualTime.started()) throw ClockException(ClockException.Reason.ALREADY_CLOCKED_IN)
        val now = nowLocal()
        if (!ClockRules.canClockIn(shift.plannedTime.start, shift.plannedTime.end, now, timeZone())) {
            throw ClockException(ClockException.Reason.TOO_EARLY)
        }
        val geofence = geofenceOf(shift)
        if (geofence != null) return@runCatching clockThroughGeofence(shiftId, ClockAction.IN, geofence, readLocation)
        shiftRepository.updateActualTime(shiftId, TimeRange(now.toMinute(), ""), userId).getOrThrow()
        ClockOutcome()
    }

    suspend fun clockOut(
        shiftId: String,
        userId: String,
        readLocation: (suspend () -> LocationReading)? = null
    ): Result<ClockOutcome> = runCatching {
        val shift = shiftRepository.getShift(shiftId).getOrThrow()
        if (!shift.isClockedBy(userId)) throw ClockException(ClockException.Reason.NOT_YOUR_SHIFT)
        val actual = shift.actualTime
        if (!actual.started() || actual.ended()) throw ClockException(ClockException.Reason.NOT_CLOCKED_IN)
        val geofence = geofenceOf(shift)
        if (geofence != null) return@runCatching clockThroughGeofence(shiftId, ClockAction.OUT, geofence, readLocation)
        // SH-134: a shift covered for a colleague is not the person's own document to write: the
        // clockShift function records it (the rules refuse the planned person's writes too).
        if (shift.userId != userId) {
            return@runCatching shiftRepository.clockWithLocation(
                shiftId, ClockAction.OUT, LocationReading.Unavailable(LocationUnavailable.UNSUPPORTED)
            ).getOrThrow()
        }
        // SH-116: a break still going on ends with the shift. The server records that; offline the
        // break stays open and every calculation ends it at the clock-out (BreakRules.intervals).
        if (BreakRules.open(shift.breaks) != null) {
            shiftRepository.clockWithLocation(shiftId, ClockAction.BREAK_END, LocationReading.Unavailable(LocationUnavailable.UNSUPPORTED))
        }
        shiftRepository.updateActualTime(shiftId, actual!!.copy(end = nowLocal().toMinute()), userId).getOrThrow()
        ClockOutcome()
    }

    /**
     * SH-165: whether clocking out [shift] now ends it more than [ClockRules.EARLY_OUT_MINUTES]
     * before its planned end (the app asks first).
     */
    fun isEarlyClockOut(shift: Shift): Boolean = ClockRules.isEarlyClockOut(shift.plannedTime.end, nowLocal(), timeZone())

    /**
     * SH-165: the Undo of a clock-out: the shift is open again (its actual end cleared), as long as
     * the clock-out is the person's own from the app and at most [ClockRules.UNDO_MINUTES] old. Where
     * the clock-out went through the clockShift function (a geofence, a shift covered for a
     * colleague) the function clears it too (UNDO_OUT); else the person's own write, which the
     * rules allow for their actual times. A break the clock-out ended stays ended.
     */
    suspend fun undoClockOut(shiftId: String, userId: String): Result<Unit> = runCatching {
        val shift = shiftRepository.getShift(shiftId).getOrThrow()
        if (!shift.isClockedBy(userId)) throw ClockException(ClockException.Reason.NOT_YOUR_SHIFT)
        val actual = shift.actualTime
        if (!actual.started() || !actual.ended()) throw ClockException(ClockException.Reason.NOT_CLOCKED_IN)
        val source = shift.clockAudit?.end?.source
        if ((source != null && source != com.evoapps.evoshift.domain.model.ClockSource.APP) ||
            !ClockRules.canUndoClockOut(actual!!.end, nowLocal(), timeZone())
        ) throw ClockException(ClockException.Reason.UNDO_TOO_LATE)
        if (shift.userId != userId || geofenceOf(shift) != null) {
            shiftRepository.undoClockOut(shiftId).getOrThrow()
        } else {
            shiftRepository.updateActualTime(shiftId, actual.copy(end = ""), userId).getOrThrow()
        }
    }

    /** SH-134: the geofence of [workplaceId] (for a clock-in without a shift), or null where none applies. */
    suspend fun geofenceAt(workplaceId: String): ActiveGeofence? {
        val workplace = workplaceRepository?.getWorkplace(workplaceId)?.getOrThrow() ?: return null
        val location = workplace.location ?: return null
        return if (GeofenceRules.isActive(workplace.settings.geofence, location)) ActiveGeofence(workplace.settings.geofence, location) else null
    }

    /**
     * SH-134: clocks in at [workplaceId] with no shift of one's own now. The server records it on
     * a colleague's shift at that time (covering) or on a new shift, both pending a manager; the
     * geofence applies as for any clock-in ([readLocation] only where the workplace has one).
     */
    suspend fun clockInWithoutShift(
        workplaceId: String,
        readLocation: (suspend () -> LocationReading)? = null
    ): Result<ClockOutcome> = runCatching {
        val geofence = geofenceAt(workplaceId)
        val reading = if (geofence != null) {
            val read = readLocation?.invoke() ?: LocationReading.Unavailable(LocationUnavailable.UNSUPPORTED)
            when (val decision = GeofenceRules.decide(geofence.settings, geofence.location, read)) {
                is GeofenceDecision.Outside -> throw ClockException(
                    ClockException.Reason.OUTSIDE_AREA, distanceMeters = decision.distanceMeters, radiusMeters = decision.radiusMeters
                )
                is GeofenceDecision.LocationRequired -> throw ClockException(
                    ClockException.Reason.LOCATION_REQUIRED, locationUnavailable = decision.reason
                )
                is GeofenceDecision.Allow -> read
            }
        } else LocationReading.Unavailable(LocationUnavailable.UNSUPPORTED)
        shiftRepository.clockInUnplanned(workplaceId, reading).getOrThrow()
    }

    /**
     * SH-116: starts a break while clocked in. Breaks are always recorded by the clockShift function
     * (server time, the same checks as the server-side clock, and the geofence where the workplace
     * has one), never written by the phone itself.
     */
    suspend fun startBreak(
        shiftId: String,
        userId: String,
        readLocation: (suspend () -> LocationReading)? = null
    ): Result<ClockOutcome> = runCatching {
        val shift = shiftRepository.getShift(shiftId).getOrThrow()
        if (!shift.isClockedBy(userId)) throw ClockException(ClockException.Reason.NOT_YOUR_SHIFT)
        if (!shift.actualTime.started() || shift.actualTime.ended()) throw ClockException(ClockException.Reason.NOT_CLOCKED_IN)
        if (BreakRules.open(shift.breaks) != null) throw ClockException(ClockException.Reason.ALREADY_ON_BREAK)
        if (shift.breaks.size >= BreakRules.MAX_BREAKS) throw ClockException(ClockException.Reason.TOO_MANY_BREAKS)
        clockBreak(shift, ClockAction.BREAK_START, readLocation)
    }

    /** SH-116: ends the break going on. */
    suspend fun endBreak(
        shiftId: String,
        userId: String,
        readLocation: (suspend () -> LocationReading)? = null
    ): Result<ClockOutcome> = runCatching {
        val shift = shiftRepository.getShift(shiftId).getOrThrow()
        if (!shift.isClockedBy(userId)) throw ClockException(ClockException.Reason.NOT_YOUR_SHIFT)
        if (!shift.actualTime.started() || shift.actualTime.ended()) throw ClockException(ClockException.Reason.NOT_CLOCKED_IN)
        if (BreakRules.open(shift.breaks) == null) throw ClockException(ClockException.Reason.NOT_ON_BREAK)
        clockBreak(shift, ClockAction.BREAK_END, readLocation)
    }

    private suspend fun clockBreak(shift: Shift, action: ClockAction, readLocation: (suspend () -> LocationReading)?): ClockOutcome {
        val geofence = geofenceOf(shift)
        if (geofence != null) return clockThroughGeofence(shift.id, action, geofence, readLocation)
        // No geofence: nothing to measure, the function records the time only.
        return shiftRepository.clockWithLocation(shift.id, action, LocationReading.Unavailable(LocationUnavailable.UNSUPPORTED)).getOrThrow()
    }

    /**
     * Reads the location once, refuses at once what the server would refuse too (Block mode), and
     * lets the clockShift function record the time with the check (its answer is the one kept).
     */
    private suspend fun clockThroughGeofence(
        shiftId: String,
        action: ClockAction,
        geofence: ActiveGeofence,
        readLocation: (suspend () -> LocationReading)?
    ): ClockOutcome {
        val reading = readLocation?.invoke() ?: LocationReading.Unavailable(LocationUnavailable.UNSUPPORTED)
        when (val decision = GeofenceRules.decide(geofence.settings, geofence.location, reading)) {
            is GeofenceDecision.Outside -> throw ClockException(
                ClockException.Reason.OUTSIDE_AREA, distanceMeters = decision.distanceMeters, radiusMeters = decision.radiusMeters
            )
            is GeofenceDecision.LocationRequired -> throw ClockException(
                ClockException.Reason.LOCATION_REQUIRED, locationUnavailable = decision.reason
            )
            is GeofenceDecision.Allow -> Unit
        }
        return shiftRepository.clockWithLocation(shiftId, action, reading).getOrThrow()
    }

    companion object {
        const val EARLY_MINUTES = ClockRules.EARLY_MINUTES

        private val shiftTimes = ClockRules.Times { s: Shift ->
            ClockRules.ShiftTimes(s.plannedTime.start, s.plannedTime.end, s.actualTime?.start, s.actualTime?.end)
        }
    }
}

/** Which shift the clock is about, for any shape of shift ([ClockStatus] is the phone's). */
sealed interface ClockPick<out T> {
    data object Idle : ClockPick<Nothing>

    data class CanClockIn<T>(val shift: T) : ClockPick<T>

    /** Clocked in at [since] ("HH:mm"). */
    data class Working<T>(val shift: T, val since: String) : ClockPick<T>

    /** Finished today, from [start] to [end] ("HH:mm"). */
    data class Done<T>(val shift: T, val start: String, val end: String) : ClockPick<T>
}

/**
 * The clock-in rules, shared by the phone ([ClockInClockOutUseCase]) and the watch (SH-99): the
 * watch holds its shifts in another shape (WidgetShift) but must offer exactly what the phone
 * then accepts.
 */
object ClockRules {
    /** Clocking in opens this long before the planned start. */
    const val EARLY_MINUTES = 60L

    /** SH-165: a clock-out more than this long before the planned end is confirmed first. */
    const val EARLY_OUT_MINUTES = 15L

    /**
     * SH-165: a clock-out can be undone this long afterwards (the snackbar offers it for about
     * 10 seconds; the margin covers a slow network). functions/geofence.js UNDO_MINUTES agrees.
     */
    const val UNDO_MINUTES = 5L

    /** SH-165: [now] is more than [EARLY_OUT_MINUTES] before [plannedEnd] (local ISO date-times). */
    fun isEarlyClockOut(plannedEnd: String, now: LocalDateTime, zone: TimeZone): Boolean {
        val end = runCatching { LocalDateTime.parse(plannedEnd) }.getOrNull() ?: return false
        return minutesBetween(now, end, zone) > EARLY_OUT_MINUTES
    }

    /** SH-165: a clock-out at [clockedOut] may still be undone at [now] (at most [UNDO_MINUTES] later). */
    fun canUndoClockOut(clockedOut: String, now: LocalDateTime, zone: TimeZone): Boolean {
        val out = runCatching { LocalDateTime.parse(clockedOut) }.getOrNull() ?: return false
        return minutesBetween(out, now, zone) <= UNDO_MINUTES
    }

    /** Whole minutes from [from] to [to] in [zone] (negative when [to] is earlier). */
    private fun minutesBetween(from: LocalDateTime, to: LocalDateTime, zone: TimeZone): Long =
        (to.toInstant(zone).toEpochMilliseconds() - from.toInstant(zone).toEpochMilliseconds()) / 60_000

    /** A shift's planned and clocked times, local ISO date-times; clocked ones empty or null when not yet. */
    data class ShiftTimes(val plannedStart: String, val plannedEnd: String, val clockedIn: String?, val clockedOut: String?)

    /** How to read [ShiftTimes] from a shift of type [T]. */
    fun interface Times<T> {
        fun of(shift: T): ShiftTimes
    }

    /** From [EARLY_MINUTES] before the planned start until the planned end. */
    fun canClockIn(plannedStart: String, plannedEnd: String, now: LocalDateTime, zone: TimeZone): Boolean {
        val start = LocalDateTime.parse(plannedStart)
        val end = LocalDateTime.parse(plannedEnd)
        val opens = start.toInstant(zone).minusMinutes(EARLY_MINUTES)
        return now.toInstant(zone) >= opens && now < end
    }

    /**
     * The clock for one person's published [shifts] at [now]: a shift clocked in and not out;
     * else the earliest one that may be clocked in now; else the last one finished today.
     */
    fun <T> pick(shifts: List<T>, now: LocalDateTime, zone: TimeZone, times: Times<T>): ClockPick<T> {
        val sorted = shifts.map { it to times.of(it) }.sortedBy { it.second.plannedStart }
        sorted.firstOrNull { (_, t) -> t.clockedIn.present() && !t.clockedOut.present() }?.let { (shift, t) ->
            return ClockPick.Working(shift, t.clockedIn!!.hhmm())
        }
        sorted.firstOrNull { (_, t) -> !t.clockedIn.present() && canClockIn(t.plannedStart, t.plannedEnd, now, zone) }?.let { (shift, _) ->
            return ClockPick.CanClockIn(shift)
        }
        // The last shift finished today stays visible until the day ends.
        sorted.lastOrNull { (_, t) -> t.clockedIn.present() && t.clockedOut.present() && LocalDateTime.parse(t.clockedOut!!).date == now.date }
            ?.let { (shift, t) -> return ClockPick.Done(shift, t.clockedIn!!.hhmm(), t.clockedOut!!.hhmm()) }
        return ClockPick.Idle
    }

    private fun String?.present() = !isNullOrEmpty()
}

/**
 * SH-134: whether [userId] clocks this shift: their own (unless a colleague covers it), or one they
 * cover for a colleague (an unplanned clock-in not rejected).
 */
fun Shift.isClockedBy(userId: String): Boolean {
    val cover = unplanned?.takeIf { it.kind == com.evoapps.evoshift.domain.model.UnplannedKind.COVER && it.status != com.evoapps.evoshift.domain.model.UnplannedStatus.REJECTED }
    return if (cover != null) cover.userId == userId else this.userId == userId
}

private fun TimeRange?.started() = this != null && start.isNotEmpty()
private fun TimeRange?.ended() = this != null && end.isNotEmpty()
private fun String.hhmm() = substringAfter('T').take(5)
private fun LocalDateTime.toMinute(): String = LocalDateTime(year, month, day, hour, minute).toString().let {
    if (it.length == 16) "$it:00" else it
}
private fun Instant.minusMinutes(minutes: Long): Instant = Instant.fromEpochMilliseconds(toEpochMilliseconds() - minutes * 60_000)
